import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Importar todos los productos desde allProducts
import { allProductsList, ProductDetail } from '../../mocks/allProducts';

interface Product {
  id: number;
  name: string;
  image: string;
  unitPrice: number;
  category: string;
  route: string;
}

export default function ProductSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState<ProductDetail[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Filtrar productos cuando cambia el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      const searchLower = searchTerm.toLowerCase();
      const filtered = allProductsList.filter(product =>
        product.name.toLowerCase().includes(searchLower) ||
        product.category.toLowerCase().includes(searchLower)
      ).slice(0, 8); // Máximo 8 resultados
      setFilteredProducts(filtered);
      setIsOpen(true);
    } else {
      setFilteredProducts([]);
      setIsOpen(false);
    }
  }, [searchTerm]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectProduct = (product: ProductDetail) => {
    setSearchTerm('');
    setIsOpen(false);
    navigate(`/producto/${product.uniqueId}`);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <section className="py-12 bg-zinc-900">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-8">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">
            Busca tu <span className="text-lime-400">Producto</span>
          </h2>
          <p className="text-gray-400 text-sm">Encuentra rápidamente lo que necesitas</p>
        </div>

        <div ref={searchRef} className="relative">
          {/* Input de búsqueda */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
              <i className="ri-search-line text-xl text-lime-400"></i>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar productos... (ej: Coca-Cola, Azúcar, Gatorade)"
              className="w-full pl-14 pr-12 py-4 bg-black/60 border-2 border-lime-500/30 rounded-xl text-white placeholder-gray-500 focus:border-lime-400 focus:ring-2 focus:ring-lime-400/20 outline-none transition-all text-base"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  inputRef.current?.focus();
                }}
                className="absolute inset-y-0 right-0 pr-5 flex items-center cursor-pointer"
              >
                <i className="ri-close-circle-fill text-xl text-gray-500 hover:text-lime-400 transition-colors"></i>
              </button>
            )}
          </div>

          {/* Dropdown de resultados */}
          {isOpen && filteredProducts.length > 0 && (
            <div className="absolute z-50 w-full mt-2 bg-zinc-900 border-2 border-lime-500/30 rounded-xl shadow-2xl shadow-lime-500/10 overflow-hidden">
              <div className="max-h-[400px] overflow-y-auto">
                {filteredProducts.map((product, index) => (
                  <button
                    key={product.uniqueId}
                    onClick={() => handleSelectProduct(product)}
                    className="w-full flex items-center gap-4 p-4 hover:bg-lime-500/10 transition-colors cursor-pointer border-b border-lime-500/10 last:border-b-0"
                  >
                    {/* Imagen del producto */}
                    <div className="w-14 h-14 bg-white rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* Info del producto */}
                    <div className="flex-1 text-left">
                      <p className="text-white font-medium text-sm line-clamp-1">{product.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 bg-lime-500/20 text-lime-400 text-xs rounded-full">
                          {product.category}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {formatPrice(product.unitPrice)} /ud
                        </span>
                      </div>
                    </div>

                    {/* Flecha */}
                    <div className="flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-xl text-lime-400"></i>
                    </div>
                  </button>
                ))}
              </div>

              {/* Footer del dropdown */}
              <div className="p-3 bg-black/40 border-t border-lime-500/20">
                <p className="text-center text-xs text-gray-500">
                  <i className="ri-information-line mr-1"></i>
                  {filteredProducts.length} producto{filteredProducts.length !== 1 ? 's' : ''} encontrado{filteredProducts.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}

          {/* Mensaje sin resultados */}
          {isOpen && searchTerm.length >= 2 && filteredProducts.length === 0 && (
            <div className="absolute z-50 w-full mt-2 bg-zinc-900 border-2 border-lime-500/30 rounded-xl shadow-2xl shadow-lime-500/10 p-8 text-center">
              <i className="ri-search-eye-line text-4xl text-gray-600 mb-3 block"></i>
              <p className="text-gray-400 text-sm">No se encontraron productos para "{searchTerm}"</p>
              <p className="text-gray-600 text-xs mt-2">Intenta con otro término de búsqueda</p>
            </div>
          )}
        </div>

        {/* Sugerencias rápidas */}
        <div className="flex flex-wrap justify-center gap-2 mt-6">
          <span className="text-gray-500 text-xs">Búsquedas populares:</span>
          {['Coca-Cola', 'Score', 'Gatorade', 'Azúcar', 'Pepsi'].map((term) => (
            <button
              key={term}
              onClick={() => setSearchTerm(term)}
              className="px-3 py-1 bg-lime-500/10 border border-lime-500/20 text-lime-400 text-xs rounded-full hover:bg-lime-500/20 transition-colors cursor-pointer"
            >
              {term}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
